// =============================================================
// GoogleIntegrationsPage  Phase 4
// Dedicated Google Services Integration Dashboard
// Connect Google OAuth, map Search Console properties,
// map Google Business Profile locations, and sync performance metrics.
// =============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  businessApi,
  googleApi,
  Business,
  GoogleStatusResponse,
  SearchConsolePropertyDto,
  SearchConsolePropertyOption,
  SearchConsoleSummaryDto,
  GoogleBusinessConnectionDto,
  GoogleBusinessLocationOption,
  GoogleBusinessSummaryDto,
} from '../lib/api';
import { GoogleConnectionCard } from '../components/google/GoogleConnectionCard';
import { SearchConsoleCard } from '../components/google/SearchConsoleCard';
import { GoogleBusinessProfileCard } from '../components/google/GoogleBusinessProfileCard';

export default function GoogleIntegrationsPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedBizId = businessId ? parseInt(businessId, 10) : undefined;

  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Success / error banners from OAuth redirect
  const [alertSuccess, setAlertSuccess] = useState<string | null>(null);
  const [alertError, setAlertError] = useState<string | null>(null);

  // Google OAuth Status
  const [googleStatus, setGoogleStatus] = useState<GoogleStatusResponse>({
    configured: false,
    connected: false,
    connection: null,
  });

  // Search Console State
  const [connectedProperty, setConnectedProperty] = useState<SearchConsolePropertyDto | null>(null);
  const [availableProperties, setAvailableProperties] = useState<SearchConsolePropertyOption[]>([]);
  const [gscSummary, setGscSummary] = useState<SearchConsoleSummaryDto | null>(null);
  const [loadingGscProps, setLoadingGscProps] = useState(false);
  const [gscPropsError, setGscPropsError] = useState<string | null>(null);
  const [syncingGsc, setSyncingGsc] = useState(false);

  // Google Business Profile State
  const [connectedLocation, setConnectedLocation] = useState<GoogleBusinessConnectionDto | null>(null);
  const [availableLocations, setAvailableLocations] = useState<GoogleBusinessLocationOption[]>([]);
  const [gbpSummary, setGbpSummary] = useState<GoogleBusinessSummaryDto | null>(null);
  const [loadingGbpLocations, setLoadingGbpLocations] = useState(false);
  const [gbpLocationsError, setGbpLocationsError] = useState<string | null>(null);
  const [syncingGbp, setSyncingGbp] = useState(false);

  // Check URL query parameters for OAuth callback banners
  useEffect(() => {
    if (searchParams.get('google_connected') === 'true') {
      setAlertSuccess('Google account successfully connected via OAuth 2.0!');
      searchParams.delete('google_connected');
      setSearchParams(searchParams, { replace: true });
    }
    const errParam = searchParams.get('google_error');
    if (errParam) {
      setAlertError(`Google connection error: ${decodeURIComponent(errParam)}`);
      searchParams.delete('google_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch Google Business Profile locations on demand
  const fetchGbpLocations = useCallback(async () => {
    if (!parsedBizId) return;
    setLoadingGbpLocations(true);
    setGbpLocationsError(null);
    try {
      const gbpLocs = await googleApi.getGoogleBusinessLocations(parsedBizId);
      const connLoc = (gbpLocs as any)?.connectedLocation ?? (gbpLocs as any)?.selectedLocation ?? null;
      const availLocs = (gbpLocs as any)?.availableLocations ?? (gbpLocs as any)?.locations ?? [];
      const apiErr = (gbpLocs as any)?.error ?? null;

      setConnectedLocation(connLoc);
      setAvailableLocations(Array.isArray(availLocs) ? availLocs : []);

      if (apiErr) {
        setGbpLocationsError(apiErr);
      } else {
        setGbpLocationsError(null);
      }

      if (connLoc) {
        try {
          const summary = await googleApi.getGoogleBusinessMetrics(parsedBizId);
          setGbpSummary(summary);
        } catch {
          // ignore
        }
      }
    } catch (e: any) {
      console.warn('Could not load GBP locations:', e.message);
      if (e.status === 403 || e.message?.includes('403') || e.message?.includes('approval')) {
        setGbpLocationsError('Google Business Profile API access may require additional approval/configuration.');
      } else if (e.status === 429 || e.message?.includes('429') || e.message?.includes('quota')) {
        setGbpLocationsError('Google Business Profile API quota limit reached. Default quota is 0 requests/min until approved by Google.');
      } else {
        setGbpLocationsError(e.message || 'Unable to load Google Business Profile locations.');
      }
      setAvailableLocations([]);
    } finally {
      setLoadingGbpLocations(false);
    }
  }, [parsedBizId]);

  // Load business and Google status
  const loadData = useCallback(async () => {
    if (!parsedBizId) return;
    setLoading(true);
    setError(null);
    setConnectedProperty(null);
    setGscSummary(null);
    setConnectedLocation(null);
    setGbpSummary(null);

    try {
      // 1. Fetch Business
      const biz = await businessApi.get(parsedBizId);
      setBusiness(biz);

      // 2. Fetch Google OAuth Status
      const statusRes = await googleApi.getStatus(parsedBizId);
      setGoogleStatus(statusRes);

      // If connected, fetch Search Console & GBP data
      if (statusRes.connected) {
        // Load Search Console
        setLoadingGscProps(true);
        setGscPropsError(null);
        try {
          const gscProps = await googleApi.getSearchConsoleProperties(parsedBizId);
          const connProp = (gscProps as any)?.connectedProperty ?? (gscProps as any)?.selectedProperty ?? null;
          const availProps = (gscProps as any)?.availableProperties ?? (gscProps as any)?.properties ?? [];
          setConnectedProperty(connProp);
          setAvailableProperties(Array.isArray(availProps) ? availProps : []);

          if (connProp) {
            try {
              const summary = await googleApi.getSearchConsoleMetrics(parsedBizId);
              setGscSummary(summary);
            } catch {
              // no metrics yet or empty
            }
          }
        } catch (e: any) {
          console.warn('Could not load Search Console properties:', e.message);
          setGscPropsError(e.message || 'Unable to load Search Console properties.');
          setAvailableProperties([]);
        } finally {
          setLoadingGscProps(false);
        }

        // Load Google Business Profile
        await fetchGbpLocations();
      }
    } catch (err: unknown) {
      console.error('Failed to load integration data:', err);
      const msg = err instanceof Error ? err.message : 'Failed to load business data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [parsedBizId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for active-business-change event from TopBar or Navigation
  useEffect(() => {
    const handleBizChange = (e: Event) => {
      const customEvent = e as CustomEvent<number>;
      if (customEvent.detail && customEvent.detail !== parsedBizId) {
        setConnectedProperty(null);
        setAvailableProperties([]);
        setGscSummary(null);
        setConnectedLocation(null);
        setAvailableLocations([]);
        setGbpSummary(null);
        setGoogleStatus({
          configured: false,
          connected: false,
          connection: null,
        });
      }
    };
    window.addEventListener('active-business-change', handleBizChange);
    return () => window.removeEventListener('active-business-change', handleBizChange);
  }, [parsedBizId]);

  // Handle OAuth initiation
  const handleConnectGoogle = async () => {
    if (!parsedBizId) return;
    try {
      const { authUrl } = await googleApi.getConnectUrl(parsedBizId);
      window.location.href = authUrl;
    } catch (err: any) {
      setAlertError(err.message || 'Failed to initiate Google OAuth');
    }
  };

  // Handle Disconnect
  const handleDisconnectGoogle = async () => {
    try {
      await googleApi.disconnect(parsedBizId);
      setAlertSuccess('Google connection disconnected. Existing historical data has been preserved.');
      setConnectedProperty(null);
      setConnectedLocation(null);
      await loadData();
    } catch (err: any) {
      setAlertError(err.message || 'Failed to disconnect Google');
    }
  };

  // Handle Select Search Console Property
  const handleSelectProperty = async (siteUrl: string) => {
    if (!parsedBizId) return;
    try {
      const saved = await googleApi.selectSearchConsoleProperty(parsedBizId, siteUrl);
      setConnectedProperty(saved);
      // Auto-trigger sync on selection
      await handleSyncSearchConsole();
    } catch (err: any) {
      setAlertError(err.message || 'Failed to select Search Console property');
    }
  };

  // Handle Sync Search Console
  const handleSyncSearchConsole = async () => {
    if (!parsedBizId) return;
    setSyncingGsc(true);
    setAlertError(null);
    try {
      const result = await googleApi.syncSearchConsole(parsedBizId);
      setAlertSuccess(`Search Console synced: ${result.metricsCount} metric rows updated (${result.period}).`);
      const summary = await googleApi.getSearchConsoleMetrics(parsedBizId);
      setGscSummary(summary);
      if (connectedProperty) {
        setConnectedProperty({ ...connectedProperty, lastSyncedAt: result.syncedAt });
      }
    } catch (err: any) {
      setAlertError(err.message || 'Failed to sync Search Console data');
    } finally {
      setSyncingGsc(false);
    }
  };

  // Handle Select GBP Location
  const handleSelectLocation = async (data: { locationId: string; locationName: string; accountId?: string }) => {
    if (!parsedBizId) return;
    try {
      const saved = await googleApi.selectGoogleBusinessLocation(parsedBizId, data);
      setConnectedLocation(saved);
      // Auto-trigger sync on selection
      await handleSyncGoogleBusiness();
    } catch (err: any) {
      setAlertError(err.message || 'Failed to select Google Business Profile location');
    }
  };

  // Handle Sync GBP
  const handleSyncGoogleBusiness = async () => {
    if (!parsedBizId) return;
    setSyncingGbp(true);
    setAlertError(null);
    try {
      const result = await googleApi.syncGoogleBusiness(parsedBizId);
      setAlertSuccess(`Google Business Profile synced: ${result.metricsCount} metrics and ${result.keywordsCount || 0} search keywords updated.`);
      const summary = await googleApi.getGoogleBusinessMetrics(parsedBizId);
      setGbpSummary(summary);
      if (connectedLocation) {
        setConnectedLocation({ ...connectedLocation, lastSyncedAt: result.syncedAt });
      }
    } catch (err: any) {
      setAlertError(err.message || 'Failed to sync Google Business Profile data');
    } finally {
      setSyncingGbp(false);
    }
  };

  return (
    <DashboardLayout
      title={business ? `${business.name}  Google Integrations` : 'Google Integrations'}
      subtitle="Connect Google Search Console and Google Business Profile via OAuth 2.0"
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        {
          label: business?.name || 'Business',
          to: business ? `/businesses/${business.id}/dashboard` : '/businesses',
        },
        { label: 'Google Integrations' },
      ]}
    >
      <div className="dashboard-container" style={{ maxWidth: '1080px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Navigation Breadcrumb */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#64748b' }}>
          <Link to="/dashboard" style={{ color: '#64748b', textDecoration: 'none' }}>
            Dashboard
          </Link>
          <span>/</span>
          {business ? (
            <Link to={`/businesses/${business.id}/dashboard`} style={{ color: '#64748b', textDecoration: 'none' }}>
              {business.name}
            </Link>
          ) : (
            <span>Business</span>
          )}
          <span>/</span>
          <span style={{ color: '#0f172a', fontWeight: 600 }}>Google Integrations</span>
        </div>

        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              Google Integrations
            </h1>
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.95rem' }}>
              Connect Google Search Console and Google Business Profile to unlock real multi-tenant SEO intelligence.
            </p>
          </div>

          {business && (
            <Link
              to={`/businesses/${business.id}/dashboard`}
              className="btn btn-secondary btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
            >
              <span></span>
              <span>Back to Business Dashboard</span>
            </Link>
          )}
        </div>

        {/* Banners */}
        {alertSuccess && (
          <div
            className="alert alert-success"
            style={{
              marginBottom: '20px',
              padding: '12px 16px',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              color: '#15803d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span></span>
              <span>{alertSuccess}</span>
            </div>
            <button
              type="button"
              onClick={() => setAlertSuccess(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#15803d', fontSize: '1.1rem' }}
            >
              
            </button>
          </div>
        )}

        {alertError && (
          <div
            className="alert alert-error"
            style={{
              marginBottom: '20px',
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span></span>
              <span>{alertError}</span>
            </div>
            <button
              type="button"
              onClick={() => setAlertError(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: '1.1rem' }}
            >
              
            </button>
          </div>
        )}

        {loading && !business ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#64748b' }}>
            <div
              className="spinner"
              style={{
                width: '32px',
                height: '32px',
                border: '3px solid #e2e8f0',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                margin: '0 auto 16px',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            Loading Google integration status...
          </div>
        ) : error ? (
          <div className="card" style={{ padding: '32px', textAlign: 'center' }}>
            <p style={{ color: '#ef4444', fontWeight: 600 }}>{error}</p>
            <button type="button" className="btn btn-secondary btn-sm" onClick={loadData}>
              Try Again
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Card 1: Organization Google Connection */}
            <GoogleConnectionCard
              configured={googleStatus.configured}
              connected={googleStatus.connected}
              connection={googleStatus.connection}
              onConnect={handleConnectGoogle}
              onDisconnect={handleDisconnectGoogle}
              loading={loading}
            />

            {/* Card 2: Google Search Console */}
            <SearchConsoleCard
              googleConnected={googleStatus.connected}
              connectedProperty={connectedProperty}
              availableProperties={availableProperties}
              summary={gscSummary}
              onSelectProperty={handleSelectProperty}
              onSync={handleSyncSearchConsole}
              loadingProperties={loadingGscProps}
              errorProperties={gscPropsError}
              syncing={syncingGsc}
              onConnectGoogle={handleConnectGoogle}
            />

            {/* Card 3: Google Business Profile */}
            <GoogleBusinessProfileCard
              googleConnected={googleStatus.connected}
              connectedLocation={connectedLocation}
              availableLocations={availableLocations}
              summary={gbpSummary}
              onSelectLocation={handleSelectLocation}
              onSync={handleSyncGoogleBusiness}
              onFetchLocations={fetchGbpLocations}
              loadingLocations={loadingGbpLocations}
              errorLocations={gbpLocationsError}
              syncing={syncingGbp}
              onConnectGoogle={handleConnectGoogle}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
